import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Save,
  Loader2,
  AlertCircle,
  Check,
  Hash,
  Calendar,
  FileText,
  Plus,
  Edit2,
  Trash2,
  X,
  Copy
} from 'lucide-react';
import { useCompany } from '../../../../contexts/CompanyContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { settingsService } from '../../../../services/settingsService';

const RESET_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly (resets each month)' },
  { value: 'yearly', label: 'Yearly (resets each year)' },
  { value: 'never', label: 'Never (continuous sequence)' }
];

const TEMPLATE_CATEGORIES = [
  'Cleaning',
  'Maintenance',
  'Landscaping',
  'Security',
  'Delivery',
  'Installation',
  'Inspection',
  'Other'
];

const JobConfigurationTab = () => {
  const { currentCompany } = useCompany();
  const { hasPermission, userRole } = usePermissions();
  // Primary owner always has full access
  const isPrimaryOrOwner = userRole === 'primary_owner' || userRole === 'owner';
  const canEditSettings = isPrimaryOrOwner || hasPermission('manage_job_templates');
  const canManageTemplates = isPrimaryOrOwner || hasPermission('manage_job_templates');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Job ID Settings
  const [settings, setSettings] = useState({
    job_id_prefix: '',
    job_id_reset_frequency: 'monthly'
  });

  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: '',
    description: '',
    isShared: false
  });

  useEffect(() => {
    if (currentCompany?.id) {
      loadData();
    }
  }, [currentCompany?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load settings - gracefully handle if table doesn't exist
      try {
        const settingsRes = await settingsService.getCompanySettings(currentCompany.id);
        setSettings({
          job_id_prefix: settingsRes.data?.job_id_prefix || '',
          job_id_reset_frequency: settingsRes.data?.job_id_reset_frequency || 'monthly'
        });
      } catch (e) {
        console.warn('Company settings not available:', e.message);
        // Use defaults
      }

      // Load templates - gracefully handle if table doesn't exist
      try {
        const templatesRes = await settingsService.getJobTemplates(currentCompany.id);
        setTemplates(templatesRes.data || []);
      } catch (e) {
        console.warn('Job templates not available:', e.message);
        setTemplates([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      // Don't show error - use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      await settingsService.updateCompanySettings(currentCompany.id, {
        job_id_prefix: settings.job_id_prefix || null,
        job_id_reset_frequency: settings.job_id_reset_frequency
      });

      setSuccess('Job settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save job settings');
    } finally {
      setSaving(false);
    }
  };

  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.template_name,
        category: template.category || '',
        description: template.description || '',
        isShared: template.is_shared
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        category: '',
        description: '',
        isShared: false
      });
    }
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      setError('Template name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingTemplate) {
        await settingsService.updateJobTemplate(editingTemplate.id, {
          name: templateForm.name,
          category: templateForm.category || null,
          description: templateForm.description || null,
          isShared: templateForm.isShared,
          data: editingTemplate.template_data
        });
      } else {
        await settingsService.createJobTemplate(currentCompany.id, {
          name: templateForm.name,
          category: templateForm.category || null,
          description: templateForm.description || null,
          isShared: templateForm.isShared,
          data: {}
        });
      }

      await loadData();
      setShowTemplateModal(false);
      setSuccess(editingTemplate ? 'Template updated' : 'Template created');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await settingsService.deleteJobTemplate(templateId);
      await loadData();
      setSuccess('Template deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    }
  };

  // Get current month abbreviation
  const getMonthAbbrev = () => {
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return months[new Date().getMonth()];
  };

  // Generate preview job ID (one-off job)
  const getPreviewJobId = () => {
    const prefix = (settings.job_id_prefix || currentCompany?.company_tag || 'JOB').toUpperCase();
    const year = String(new Date().getFullYear()).slice(-2); // '26' from 2026
    const monthAbbrev = getMonthAbbrev(); // 'JAN'

    switch (settings.job_id_reset_frequency) {
      case 'monthly':
        // Format: CONTINU-JAN26-1
        return `${prefix}-${monthAbbrev}${year}-1`;
      case 'yearly':
        // Format: CONTINU-2026-1
        return `${prefix}-20${year}-1`;
      case 'never':
      default:
        // Format: CONTINU-1
        return `${prefix}-1`;
    }
  };

  // Generate preview for recurring job instance
  const getPreviewInstanceId = () => {
    const prefix = (settings.job_id_prefix || currentCompany?.company_tag || 'JOB').toUpperCase();
    const year = String(new Date().getFullYear()).slice(-2);
    const monthAbbrev = getMonthAbbrev();

    switch (settings.job_id_reset_frequency) {
      case 'monthly':
        // Format: CONTINU-JAN26-1-#1
        return `${prefix}-${monthAbbrev}${year}-1-#1`;
      case 'yearly':
        // Format: CONTINU-2026-1-#1
        return `${prefix}-20${year}-1-#1`;
      case 'never':
      default:
        // Format: CONTINU-1-#1
        return `${prefix}-1-#1`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Job Configuration</h2>
          <p className="text-text-secondary text-sm mt-1">
            Configure job ID format and manage job templates
          </p>
        </div>
        {!canEditSettings && (
          <span className="px-3 py-1 bg-border/50 text-text-secondary rounded-full text-xs">
            View Only
          </span>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <Check size={16} className="text-green-500" />
          <span className="text-green-400 text-sm">{success}</span>
        </div>
      )}

      {/* Job ID Settings */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Hash size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Job ID Configuration</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Custom Prefix (Optional)</label>
            <input
              type="text"
              value={settings.job_id_prefix}
              onChange={(e) => setSettings({ ...settings, job_id_prefix: e.target.value.toUpperCase() })}
              disabled={!canEditSettings}
              maxLength={10}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed uppercase"
              placeholder={currentCompany?.company_tag || 'JOB'}
            />
            <p className="text-xs text-text-secondary mt-1">
              Leave empty to use company tag ({currentCompany?.company_tag})
            </p>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Calendar size={14} className="inline mr-1" /> Reset Frequency
            </label>
            <select
              value={settings.job_id_reset_frequency}
              onChange={(e) => setSettings({ ...settings, job_id_reset_frequency: e.target.value })}
              disabled={!canEditSettings}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {RESET_FREQUENCIES.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-4 p-4 bg-background border border-border rounded-lg">
          <p className="text-text-secondary text-xs mb-2">Preview</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary text-sm">One-off job:</span>
              <span className="text-text-primary font-mono">{getPreviewJobId()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary text-sm">Recurring instance:</span>
              <span className="text-text-primary font-mono">{getPreviewInstanceId()}</span>
            </div>
          </div>
        </div>

        {/* Coming Soon notice for yearly/never */}
        {settings.job_id_reset_frequency !== 'monthly' && (
          <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
            <p className="text-orange-400 text-sm">
              <span className="font-medium">Coming Soon:</span> Yearly and Never reset options are not yet active. Currently, job IDs reset monthly.
            </p>
          </div>
        )}

        {canEditSettings && (
          <div className="flex justify-end mt-4">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save Settings
            </button>
          </div>
        )}
      </div>

      {/* Job Templates */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <FileText size={16} className="text-text-secondary" />
            <h3 className="text-sm font-medium text-text-secondary">Job Templates</h3>
          </div>
          {canManageTemplates && (
            <button
              onClick={() => openTemplateModal()}
              className="px-3 py-1.5 bg-surface text-text-primary rounded-lg text-sm hover:bg-surface-light transition-colors flex items-center"
            >
              <Plus size={14} className="mr-1" />
              New Template
            </button>
          )}
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-12 bg-background border border-border rounded-lg">
            <FileText size={40} className="mx-auto text-text-tertiary mb-3" />
            <p className="text-text-secondary text-sm">No templates yet</p>
            <p className="text-text-secondary text-xs mt-1 max-w-sm mx-auto">
              Job templates help you quickly create similar jobs with predefined settings.
              {canManageTemplates ? ' Click "New Template" to create your first template.' : ''}
            </p>
            {/* Example templates preview */}
            <div className="mt-6 text-left max-w-md mx-auto space-y-2">
              <p className="text-text-secondary text-xs uppercase tracking-wider mb-3">Example templates:</p>
              <div className="p-3 bg-surface border border-border/50 rounded-lg opacity-60">
                <div className="flex items-center space-x-2">
                  <span className="text-text-primary text-sm">Weekly Office Cleaning</span>
                  <span className="px-2 py-0.5 bg-border text-text-secondary rounded text-xs">Cleaning</span>
                </div>
                <p className="text-text-secondary text-xs mt-1">Standard weekly cleaning for office buildings</p>
              </div>
              <div className="p-3 bg-surface border border-border/50 rounded-lg opacity-60">
                <div className="flex items-center space-x-2">
                  <span className="text-text-primary text-sm">Quarterly HVAC Maintenance</span>
                  <span className="px-2 py-0.5 bg-border text-text-secondary rounded text-xs">Maintenance</span>
                </div>
                <p className="text-text-secondary text-xs mt-1">Scheduled HVAC system check and service</p>
              </div>
              <div className="p-3 bg-surface border border-border/50 rounded-lg opacity-60">
                <div className="flex items-center space-x-2">
                  <span className="text-text-primary text-sm">Site Security Patrol</span>
                  <span className="px-2 py-0.5 bg-border text-text-secondary rounded text-xs">Security</span>
                </div>
                <p className="text-text-secondary text-xs mt-1">Routine security patrol and inspection</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map(template => (
              <div
                key={template.id}
                className="flex items-center justify-between p-4 bg-background border border-border rounded-lg hover:border-border transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-text-primary font-medium truncate">{template.template_name}</p>
                    {template.category && (
                      <span className="px-2 py-0.5 bg-border text-text-secondary rounded text-xs">
                        {template.category}
                      </span>
                    )}
                    {template.is_shared && (
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs">
                        Shared
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-text-secondary text-sm mt-1 truncate">{template.description}</p>
                  )}
                  <p className="text-text-tertiary text-xs mt-1">
                    Created by {template.created_by_user?.full_name || 'Unknown'}
                  </p>
                </div>
                {canManageTemplates && (
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => openTemplateModal(template)}
                      className="p-2 text-text-secondary hover:text-text-primary transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="p-2 text-text-secondary hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">
                {editingTemplate ? 'Edit Template' : 'New Template'}
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-text-secondary hover:text-text-primary"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Template Name *</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                  placeholder="e.g., Weekly Office Clean"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Category</label>
                <select
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                >
                  <option value="">Select category...</option>
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Description</label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary resize-none"
                  placeholder="Brief description of this template..."
                />
              </div>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={templateForm.isShared}
                  onChange={(e) => setTemplateForm({ ...templateForm, isShared: e.target.checked })}
                  className="rounded border-border text-orange-500 focus:ring-orange-500 bg-surface"
                />
                <span className="text-text-secondary text-sm">Share with all team members</span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                {editingTemplate ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobConfigurationTab;
